import { configureStore } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/dist/query'
import { listenerMiddleware } from './typedListenerMiddleware'
import { contextSlice } from './contextSlice'
import { themeSlice } from './themeSlice'
import { sidebarSlice } from './features/sidebarSlice'
import { headerSlice } from './features/headerSlice'
import { ajaxApi } from './services/ajax'
import { departmentsApi } from './departmentsApi'
import { fleetManagementSlice } from './features/fleetManagementSlice'
import { breakdownDetailsSlice } from './features/breakdownDetailsSlice'
import { accidentManagementApi } from './services/accidentManagementApi'
import { dashboardApi } from './services/dashboardApi'
import { controllersApi } from './controllersApi'
import { reservationEditFormSlice } from './features/resEditFormSlice'
import { reservationVehicleSlice } from './features/resVehicleSlice'
import { resApprovalSlice } from './features/resApprovalSlice'
import { contextDropDownsSlice } from './features/contextDropDownsSlice'
import { reservationLocationSlice } from './features/resLocationsSlice'
import { businessDefaultsSlice } from './features/resDefaultsSlice'
import { resUiSlice } from './features/resUiSlice'
import { logisticsSlice } from './features/logisticsSlice'
import { combineReducers, PreloadedState } from 'redux'
import { contractHireApi } from './services/contractHire'
import { fleetManagementApi } from './services/fleetManagement'
import { bulkVehicleOrderApi } from './services/bulkVehicleOrder'
import { damageManagerApi } from './services/damageManager'
import { fineManagerApi } from './services/fineManager'
import { tariffBandApi } from './services/tariffBand'
import { fodRideSlice } from './features/fodRideSlice'
import { rentalApi } from './services/rentalApi'
import { journeyPlannerSlice } from '../../src/react-apps/store/journeyPlannerSlice'
import { modalSlice } from '../../src/react-apps/store/modalSlice'
import { reportingChartsSlice } from '../../src/react-apps/store/reportingChartsSlice'
import { multipleQueriesApi } from './services/multipleQueriesApi'
import { rentalReservationApi } from './services/rentalReservationApi'

export const rootReducer = combineReducers({
	globalState: contextSlice.reducer,
	theme: themeSlice.reducer,
	sidebar: sidebarSlice.reducer,
	header: headerSlice.reducer,
	fleetManagementSlice: fleetManagementSlice.reducer,
	vehicle: reservationVehicleSlice.reducer,
	breakdownDetails: breakdownDetailsSlice.reducer,
	contextDropDowns: contextDropDownsSlice.reducer,
	resbusinessDefault: businessDefaultsSlice.reducer,
	resUi: resUiSlice.reducer,
	resEditForm: reservationEditFormSlice.reducer,
	resApproval: resApprovalSlice.reducer,
	resLocation: reservationLocationSlice.reducer,
	logisticsData: logisticsSlice.reducer,
	fodRide: fodRideSlice.reducer,
	[departmentsApi.reducerPath]: departmentsApi.reducer,
	[ajaxApi.reducerPath]: ajaxApi.reducer,
	[accidentManagementApi.reducerPath]: accidentManagementApi.reducer,
	[dashboardApi.reducerPath]: dashboardApi.reducer,
	[controllersApi.reducerPath]: controllersApi.reducer,
	[contractHireApi.reducerPath]: contractHireApi.reducer,
	[fleetManagementApi.reducerPath]: fleetManagementApi.reducer,
	[bulkVehicleOrderApi.reducerPath]: bulkVehicleOrderApi.reducer,
	[damageManagerApi.reducerPath]: damageManagerApi.reducer,
	[fineManagerApi.reducerPath]: fineManagerApi.reducer,
	[tariffBandApi.reducerPath]: tariffBandApi.reducer,
	[rentalApi.reducerPath]: rentalApi.reducer,
	[multipleQueriesApi.reducerPath]: multipleQueriesApi.reducer,
	[rentalReservationApi.reducerPath]: rentalReservationApi.reducer,
	journeyPlanner: journeyPlannerSlice.reducer,
	modal: modalSlice.reducer,
	reportingCharts: reportingChartsSlice.reducer,
})

export const middleWares = [
	ajaxApi.middleware,
	departmentsApi.middleware,
	controllersApi.middleware,
	dashboardApi.middleware,
	accidentManagementApi.middleware,
	damageManagerApi.middleware,
	fineManagerApi.middleware,
	tariffBandApi.middleware,
	multipleQueriesApi.middleware,
	fleetManagementApi.middleware,
	rentalReservationApi.middleware,
]

export function setupStore(preloadedState?: PreloadedState<RootState> | {}) {
	return configureStore({
		reducer: rootReducer,
		preloadedState,
		devTools: process.env.NODE_ENV !== 'production',
		middleware: getDefaultMiddleware =>
			getDefaultMiddleware({
				serializableCheck: false,
			})
				.prepend(listenerMiddleware.middleware)
				.concat(...middleWares),
	})
}

const store = setupStore()
setupListeners(store.dispatch)

export type RootState = ReturnType<typeof rootReducer>
export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = AppStore['dispatch']

export default store
